import type { Address } from '@apparatix/types/Contacts';

export const useStringFunctions = () => {
  /**
   *
   * @description Check to see if a string is functionally empty
   * @param test string to check
   * @param includeWhitespace if true, a non-empty string that only contains whitespace will be considered empty
   * @returns true if the string is empty
   */
  const isEmptyString = (test: string, includeWhitespace: boolean = false): boolean => {
    if (test === null || test === undefined) {
      return true;
    }

    if (typeof test !== 'string') {
      throw new Error(`Error: '${test}' is type ${typeof test}, expected instance of string, null, or undefined`);
    }

    if (includeWhitespace && test.match(/^\s*$/)) {
      return true;
    }

    return test === '' || test.length === 0;
  };

  /**
   *
   * @description Compare whether two strings are equal
   * @param a first test string
   * @param b second test string
   * @param ignoreCase if true, case will be ignored when comparing. Eg a: 'Cat', b: 'cat' will return true
   * @param allowFalsy if true a: null, b: undefined will be considered equal
   * @returns true if strings are equal
   */
  const areEqual = (a: string, b: string, ignoreCase = false, allowFalsy = false): boolean => {
    if (ignoreCase) {
      return a.toLocaleLowerCase() === b.toLocaleLowerCase();
    }

    if (allowFalsy && isEmptyString(a) && isEmptyString(b)) {
      return true;
    }

    return a === b;
  };

  /**
   * @description takes a number, and returns a currency string.
   * @param amount
   * @param showDecimals if false, output will round the amount to the nearest whole number
   * @param currency
   * @returns a string formatted as currency. eg 37.14 => '$37.14'. 100000 => '$100,000'
   */
  const formatAsCurrency = (amount: number | string, showDecimals = true, currency = 'USD') => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency', currency: currency, maximumFractionDigits: showDecimals ? 2 : 0,
    });

    if (typeof amount === 'string' && isEmptyString(amount, true)) return '';

    let amountNumber: number;
    if (typeof amount === 'number') {
      amountNumber = amount;
    } else {
      try {
        amountNumber = parseFloat(amount);
      } catch {
        console.error(`Unable to parse ${amount.toString()} to number`);
        amountNumber = 0;
      }
    }

    return formatter.format(amountNumber);
  };

  /**
   * @description Removes everything but the numbers from a phone number
   * @param phone Input number
   * @returns Only the digits of a phone number
   */
  const normalizePhoneNumber = (phone: string): string => phone.replace(/[^0-9]/g, '');

  /**
   * @description Removes all HTML tags from a string
   * @param input Input string containing HTML elements
   * @param replaceWith string to replace HTML element with
   */
  const removeHtml = (input: string, replaceWith?: string): string => input?.replace(/<[^>]*>/g, replaceWith ?? '');

  /**
   * @description Takes input that may be a string or a number, and always returns a number
   */
  const ensureNumber = (input: string | number): number => {
    if (typeof input === 'number') return input;
    return parseInt(input, 10);
  };

  /**
   *
   * @description Takes input that may be a string or a number, and always returns a string
   */
  const ensureString = (input: string | number): string => {
    if (typeof input === 'string') return input;
    return input.toString(10);
  };

  /**
   * @description Takes an address object, and returns it as a single line string
   * @returns 123 Easy St, New York, NY 12345
   */
  const formatAddress = (address: Address) => {
    if (!address) return '';
    if (
      isEmptyString(address.address_line1) &&
      isEmptyString(address.city) &&
      isEmptyString(address.region) &&
      isEmptyString(address.postal_code)
    ) {
      return '';
    }
    return `${address.address_line1 ?? ''}, ${address.city ?? ''}, ${address.region ?? ''} ${address.postal_code ?? ''}`;
  };


  /**
   * @description Given a url string format it into a cl
   * @param rawUrl url
   * @returns string to be used in a link
   */
  const formattedUrl = (rawUrl: string) => {
    let url = rawUrl.trim() ?? '';

    if (!(/^https?:\/\//i).test(url)) {
      url = `https://${url}`;
    }

    try {
      const parsedUrl = new URL(url);

      return parsedUrl.href;
    } catch {
      return '';
    }
  };

  type Nameable = {
    fullName?: string;
    firstName?: string;
    lastName?: string;
    c2Name?: string;
    companyName?: string;
  }

  const getFullName = <T extends Nameable>(input: T, noValueLabel: string = 'Unnamed Contact'): string => {
    if (!input) return '';

    const _extends = {
      fullName: ('fullName' in input),
      firstName: ('firstName' in input),
      lastName: ('lastName' in input),
      c2Name: ('c2Name' in input),
      companyName: ('companyName' in input),
    };

    if (_extends.fullName && !isEmptyString(input.fullName)) {
      return input.fullName;
    }

    if (_extends.firstName && _extends.lastName && !isEmptyString(input.firstName) && !isEmptyString(input.lastName)) {
      return `${input.firstName} ${input.lastName}`;
    }

    if (_extends.firstName && !isEmptyString(input.firstName)) {
      return input.firstName;
    }

    if (_extends.lastName && !isEmptyString(input.lastName)) {
      return input.lastName;
    }

    if (_extends.c2Name && !isEmptyString(input.c2Name)) {
      return input.c2Name;
    }

    if (_extends.companyName && !isEmptyString(input.companyName)) {
      return input.companyName;
    }

    return noValueLabel;
  };

  /**
   *
   * @description capitalizes the first letter of a string
   */
  const capitalize = (input: string) => {
    if (!input) return undefined;
    const first = input.charAt(0);
    const rest = input.slice(1);
    return `${first.toUpperCase()}${rest}`;
  };

  /**
   *
   * @description takes a number and returns ordinal version. 1 -> 1st, 22 -> 22nd, 9 -> 9th
   */
  const formatNumberAsOrdinal = (toFormat: number | string) => {
    if (!toFormat) return undefined;

    if (isNaN(Number(toFormat))) {
      throw new Error('Invalid input');
    }

    const number = String(toFormat);

    if (number.endsWith('11')) {
      return `${number}th`;
    }

    if (number.endsWith('1')) {
      return `${number}st`;
    }

    if (number.endsWith('2') && number !== '12') {
      return `${number}nd`;
    }

    if (number.endsWith('3') && number !== '13') {
      return `${number}rd`;
    }

    return `${number}th`;
  };

  /**
   * @description Joins an array of strings into a single string with Oxford comma punctuation. Eg "apples, oranges, bananas, and peaches"
   * @param values array of strings to join
   * @returns string
   */
  const oxfordCommaJoin = (values: string[]) => values.reduce((acc, current, idx) => {
    const { length } = values;

    if (idx === 0) {
      return current;
    }

    if (idx === length - 1) {
      return length === 2 ? `${acc} and ${current}` : `${acc}, and ${current}`;
    }

    return `${acc}, ${current}`;
  });

  const getNumberAtEndOfString = (str: string): number => {
    const clean = str.replace(/\s/g, '');
    const numbers = [];

    for (let i=clean.length - 1; i>0; i--) {
      const n = Number(clean[i]);
      if (isNaN(n)) {
        break;
      }
      numbers.unshift(n);
    }

    if (numbers.length === 0) {
      return undefined;
    }

    return Number(numbers.join(''));
  };

  /**
   * @description Get the first letter of each word in a string
   * @param str input string
   * @param max limit the returned characters to no more than max
   * @param separator character used to split words
   * @returns first character of each word in the input string, or empty string if input is empty or not a string
   */
  const getInitials = (str: string, max: number = 2, separator: string = ' ') =>
    isEmptyString(str, true)
      ? ''
      : str.split(separator).map(word => word.charAt(0)).join('').slice(0, max);

  /**
   * @description Make a good guess at whether a string contains HTML. Will return true for false positive edge cases,
   * such as: 'This is <<important>>', or 'Dear <customer name here>'. It also will NOT detect strings that are
   * technically HTML, but contain no tags, such as 'This contains &ldquo;quoted&rdquo; text'
   * Note that this will also likely test positive for any sort of XML
   */
  const containsHtml = (test: string): boolean =>
    typeof test === 'string' && test?.match(/<(?:\/|!)?[a-z\-*][\w-]*(?:\s[^>]*)?>/ig)?.length > 0;

  /**
   * @description Display plural or singular form of word depending on how many items there are
   * "1 apple" or "5 apples"
   * @param count Number of items
   * @param singular Singular form of word
   * @param plural Plural form of word
   * @param textOnlyWhenZero when true, shows "No apples" instead of "0 apples". Defaults to true
   * @returns Count number followed by either singular or plural form of word
   */
  const conditionallyPluralize = (count: number, singular: string, plural: string, textOnlyWhenZero: boolean = true) => {
    if (count <= 0) {
      return textOnlyWhenZero ? `No ${plural}` : `0 ${plural}`;
    }

    if (count === 1) {
      return `1 ${singular}`;
    }

    return `${count} ${plural}`;
  };

  /**
   * @description Caps a number to n+ when number exceeds cap
   * "99+"
   * @param count Actual value
   * @param capAfter Max value (inclusive) to display before capping. Defaults to 99
   * @returns Original count (as string) when less than cap, otherwise <cap>+
   */
  const capCount = (count: number, capAfter: number = 99) => count <= capAfter ? String(count) : `${capAfter}+`;

  return {
    areEqual,
    capCount,
    capitalize,
    conditionallyPluralize,
    containsHtml,
    ensureNumber,
    ensureString,
    formatAddress,
    formatAsCurrency,
    formatNumberAsOrdinal,
    formattedUrl,
    getFullName,
    getInitials,
    getNumberAtEndOfString,
    isEmptyString,
    normalizePhoneNumber,
    oxfordCommaJoin,
    removeHtml,
  };
};
